import {DEFAULT_PERMISSIONS_TEMPLATE_ID, UserManagementConstants} from '../../../constants'
import {arrayToSet, filterMapByKeys} from '../../../atUtils'
import {UserTeamAssignmentType} from '../constants'
import _ from 'underscore'

export function makeDefaultUserAccountSettings(userManagementData, showAPStatusControl) {
    const defaultTempl = userManagementData.permissionsTemplates.find((template) => template.id === DEFAULT_PERMISSIONS_TEMPLATE_ID);
    const defaultPermissions = defaultTempl ? defaultTempl.permissions : []; // get default permission from default permissions template

    return {
        id: null,
        firstName: "",
        lastName: "",
        middleInitial: "",
        username: "",
        enabled: true,
        enabledInOtherProduct: showAPStatusControl ? true : null,
        userGroupId: UserManagementConstants.USER_GROUP_NOT_ASSIGNED_ID,
        timeZoneGroupId: UserManagementConstants.DEFAULT_TIME_ZONE_GROUP_ID,
        overtimeLevel: window.SystemSettings.defaultOvertimeLevel,
        email: "",
        hireDate: window.currentUserTime().toJSON(),
        releaseDate: null,
        initialSchedule: userManagementData.defaultWorkSchedule.slice(0),
        defaultInitialSchedule: true,
        workSchedule: [],
        rates: userManagementData.viewerSettings.enabledFeatures[UserManagementConstants.URAFeature] ? [] : null,
        accessRights: defaultPermissions,
        requiresTTApproval: false,
    }
}

/**
 * Converts user management assignments data to RPC format
 * @param data with 5 arrays with assignments and all scope flag ids
 * @param customerMap map of tasks
 * @param projectMap map of tasks
 * @param tasksMap map of tasks
 * @return user management assignments
 */
export function createManagementAssignmentsData(data, customerMap, projectMap, tasksMap) {
    const customerAssignments = []
    data.addedCustomerIds.forEach((id) => customerAssignments.push({id: id, includeSelf: true}))
    data.addedCustomerIdsExcludingSelf.forEach((id) => customerAssignments.push({id: id, includeSelf: false}))
    const projectAssignments = []
    data.addedProjectIds.forEach((id) => projectAssignments.push({id: id, includeSelf: true}))
    data.addedProjectIdsExcludingSelf.forEach((id) => projectAssignments.push({id: id, includeSelf: false}))
    const taskAssignments = []
    data.addedTaskIds.forEach((id) => taskAssignments.push({id: id, includeSelf: true}))

    return {
        managementAssignmentsSelection: data.managementAssignmentsSelection,
        customers: customerMap,
        projects: projectMap,
        tasks: tasksMap,
        customerAssignments: customerAssignments,
        projectAssignments: projectAssignments,
        taskAssignments: taskAssignments,
        taskCountsByProjects: data.taskCountsByProjects,
        projectCountsByCustomers: data.projectCountsByCustomers
    }
}

/**
 * Converts user management assignments data from RPC format to
 * @param userManageAssignmentsData user management assignments data from RPC
 * @return {{canManageAllScope: (boolean), addedTaskIds: Array, addedProjectIds: Array, addedProjectIdsExcludingSelf: Array, addedCustomerIds: Array, addedCustomerIdsExcludingSelf: Array}}
 */
export function convertManageAssignmentsToArrays(userManageAssignmentsData) {
    const manageAssignments = {
        managementAssignmentsSelection: userManageAssignmentsData.managementAssignmentsSelection,
        addedTaskIds: [],
        addedProjectIds: [],
        addedProjectIdsExcludingSelf: [],
        addedCustomerIds: [],
        addedCustomerIdsExcludingSelf: [],
        taskCountsByProjects: userManageAssignmentsData.taskCountsByProjects.map,
        projectCountsByCustomers: userManageAssignmentsData.projectCountsByCustomers.map
    }
    userManageAssignmentsData.customerAssignments.forEach((assignment) => {
        if (assignment.includeSelf)
            manageAssignments.addedCustomerIds.push(assignment.id)
        else
            manageAssignments.addedCustomerIdsExcludingSelf.push(assignment.id)
    })
    userManageAssignmentsData.projectAssignments.forEach((assignment) => {
        if (assignment.includeSelf)
            manageAssignments.addedProjectIds.push(assignment.id)
        else
            manageAssignments.addedProjectIdsExcludingSelf.push(assignment.id)
    })
    userManageAssignmentsData.taskAssignments.forEach((assignment) => manageAssignments.addedTaskIds.push(assignment.id))
    return manageAssignments
}

export function mergeAssignments(assignments, {added = [], removed = []}) {
    let processedSet = {
        ...arrayToSet(assignments),
        ...arrayToSet(added)
    }
    return Object.keys(filterMapByKeys(processedSet, removed)).map(Number)
}

export function getTaskIdsOfCustomersAndProjects(tasksMap = {}, customerIds = [], projectIds = []) {
    const cIdsSet = arrayToSet(customerIds)
    const pIdsSet = arrayToSet(projectIds)

    return Object.values(tasksMap)
                 .filter((task) => {
                     return !!cIdsSet[task.customerId] || !!pIdsSet[task.projectId]
                 })
                 .map(task => task.id)
}

export function calculateProjectsByCustomerIds(customerIds = [], projectsMap = {}) {
    const map = {}
    customerIds.forEach(cId => {
        map[cId] = 0
    })

    Object.values(projectsMap).forEach((p) => {
        const cId = p.customerId
        if (map[cId] !== undefined) {
            map[cId] = map[cId] + 1
        }
    })
    return map
}

export const getAssignedUsersCount = ({userTeamAssignmentsData, allUsers, isUGREnabled}) => {
    const ta = userTeamAssignmentsData
    const userInAssignedGroups = isUGREnabled ? allUsers.filter(user => ta.assignedGroups.includes(user.userGroupId)).length : 0
    return ta.teamAssignmentType === UserTeamAssignmentType.SELECTED ? ta.assignedUsers.length + userInAssignedGroups : 0
}



export const areRatesEqual = (rates1, rates2) => {
    if (rates1 === null && rates2 === null)
        return true

    if (rates1.length !== rates2.length)
        return false

    for (let i = 0; i < rates1.length; i++) {
        const rate1 = rates1[i]
        const rate2 = rates2[i]

        if (rate1.rateId !== rate2.rateId)
            return false
        if (rate1.effectiveDate !== rate2.effectiveDate)
            return false

        if ((rate1.regularRate || rate2.regularRate) && rate1.regularRate !== rate2.regularRate)
            return false
        if ((rate1.overtimeRate || rate2.overtimeRate) && rate1.overtimeRate !== rate2.overtimeRate)
            return false

        const leaveRates1 = rate1.leaveRates.filter(v => v.rate).sort((v1, v2) => v1.leaveTypeId - v2.leaveTypeId)
        const leaveRates2 = rate2.leaveRates.filter(v => v.rate).sort((v1, v2) => v1.leaveTypeId - v2.leaveTypeId)
        if (!_.isEqual(leaveRates1, leaveRates2))
            return false
    }

    return true
}


// WEBPACK FOOTER //
// ./web/js/panels/user/createPanel/utils.js